import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import { getApiErrorMessage } from '@/lib/apiError';
import { useSearchParams } from 'react-router-dom';
import { Mail, Loader2, RefreshCw, Plug, ChevronDown, ShieldCheck, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { Switch } from '@/features/shared/components/ui/switch';
import { Input } from '@/features/shared/components/ui/input';
import { Label } from '@/features/shared/components/ui/label';
import {
  listIntegrations,
  startGoogleConnect,
  startMicrosoftConnect,
  createImapIntegration,
  testIntegration,
  enableIntegration,
  disableIntegration,
  syncIntegration,
  deleteIntegration,
  type EmailIntegration,
  type MailboxPurpose,
} from '@/lib/emailIntegrationsApi';

const PURPOSES: MailboxPurpose[] = ['documents', 'leave', 'payroll', 'support', 'hr_general'];

const statusVariant = (s: EmailIntegration['status']): 'success' | 'warning' | 'danger' | 'neutral' =>
  s === 'connected' ? 'success' : s === 'error' ? 'danger' : s === 'pending' ? 'warning' : 'neutral';

const providerLabel = (p: EmailIntegration['provider']) =>
  p === 'google_workspace' ? 'Google' : p === 'microsoft_365' ? 'Microsoft' : 'IMAP';

export const EmailIntegrationsPage: React.FC = () => {
  const { t } = useTranslation('settings');
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();

  const [integrations, setIntegrations] = useState<EmailIntegration[]>([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState<'google' | 'microsoft' | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [purpose, setPurpose] = useState<MailboxPurpose>('documents');

  const tk = (key: string, fallback: string, opts?: Record<string, unknown>) =>
    t(`email_integrations.${key}`, fallback, opts) as string;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setIntegrations(await listIntegrations());
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, tk('errors.load', 'Failed to load mailboxes')));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Handle the OAuth callback landing (?provider=google&status=connected|error&detail=...).
  useEffect(() => {
    const status = searchParams.get('status');
    if (!status) return;
    const provider = searchParams.get('provider') ?? 'mailbox';
    const detail = searchParams.get('detail') ?? '';
    if (status === 'connected') {
      toast.success(tk('toast.connected', '{{provider}} mailbox connected', { provider: providerLabel(provider as any) }));
      void load();
    } else if (status === 'error') {
      toast.error(tk('toast.failed', 'Connection failed: {{detail}}', { detail: decodeURIComponent(detail) }));
    }
    // clear the query so a refresh doesn't re-toast
    searchParams.delete('status');
    searchParams.delete('provider');
    searchParams.delete('detail');
    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConnect = async (provider: 'google' | 'microsoft') => {
    setConnecting(provider);
    try {
      const start = provider === 'google' ? startGoogleConnect : startMicrosoftConnect;
      const label = provider === 'google' ? 'Google Mailbox' : 'Outlook Mailbox';
      const url = await start(purpose, label, window.location.href);
      window.location.href = url; // hand off to the provider consent screen
    } catch (e: any) {
      setConnecting(null);
      const msg = e?.response?.status === 503
        ? tk('errors.provider_unconfigured', '{{provider}} connect is not configured yet', {
            provider: provider === 'google' ? 'Google' : 'Microsoft',
          })
        : getApiErrorMessage(e, tk('errors.connect', 'Could not start the connection'));
      toast.error(msg);
    }
  };

  const act = async (id: string, fn: () => Promise<unknown>, okMsg: string) => {
    setBusyId(id);
    try {
      await fn();
      toast.success(okMsg);
      await load();
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, tk('errors.action', 'Action failed')));
    } finally {
      setBusyId(null);
    }
  };

  const handleTest = (i: EmailIntegration) =>
    act(i.id, async () => {
      const r = await testIntegration(i.id);
      if (!r.ok) throw new Error(r.error || 'test failed');
    }, tk('toast.tested', 'Connection OK'));

  const handleToggle = (i: EmailIntegration) =>
    act(
      i.id,
      () => (i.enabled ? disableIntegration(i.id) : enableIntegration(i.id)),
      i.enabled ? tk('toast.disabled', 'Sync paused') : tk('toast.enabled', 'Sync enabled'),
    );

  const handleSync = (i: EmailIntegration) =>
    act(i.id, () => syncIntegration(i.id), tk('toast.syncing', 'Sync started'));

  const handleDisconnect = async (i: EmailIntegration) => {
    const ok = await confirm({
      title: 'Disconnect mailbox',
      message: tk('confirm.disconnect', 'Disconnect {{email}}? This revokes access and removes its imported mail.', {
        email: i.emailAddress,
      }),
      confirmLabel: 'Disconnect',
      danger: true,
    });
    if (!ok) return;
    return act(i.id, () => deleteIntegration(i.id), tk('toast.disconnected', 'Mailbox disconnected'));
  };

  return (
    <div className="space-y-6">
      <div className="border-b border-border pb-6">
        <h1 className="text-h1 text-charcoal">{tk('title', 'Email Integrations')}</h1>
        <p className="text-muted-foreground mt-1">
          {tk('subtitle', 'Connect a company mailbox so incoming email becomes HR actions automatically.')}
        </p>
      </div>

      {/* Connect */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plug className="w-5 h-5" /> {tk('connect.title', 'Connect a mailbox')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-2 max-w-xs">
            <Label htmlFor="purpose">{tk('connect.purpose', 'What is this mailbox for?')}</Label>
            <select
              id="purpose"
              value={purpose}
              onChange={(e) => setPurpose(e.target.value as MailboxPurpose)}
              className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
            >
              {PURPOSES.map((p) => (
                <option key={p} value={p}>
                  {tk(`purpose.${p}`, p)}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button onClick={() => handleConnect('google')} disabled={connecting !== null}>
              {connecting === 'google' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              {tk('connect.google', 'Connect Google')}
            </Button>
            <Button variant="outline" onClick={() => handleConnect('microsoft')} disabled={connecting !== null}>
              {connecting === 'microsoft' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
              {tk('connect.microsoft', 'Connect Microsoft')}
            </Button>
          </div>

          <ImapConnect purpose={purpose} onConnected={load} tk={tk} />
        </CardContent>
      </Card>

      {/* List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" /> {tk('list.title', 'Connected mailboxes')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> {tk('list.loading', 'Loading…')}
            </div>
          ) : integrations.length === 0 ? (
            <p className="py-8 text-center text-muted-foreground">{tk('list.empty', 'No mailboxes connected yet.')}</p>
          ) : (
            <div className="space-y-3">
              {integrations.map((i) => (
                <div
                  key={i.id}
                  className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border border-border rounded-lg"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-charcoal truncate">{i.emailAddress}</span>
                      <Badge variant="neutral">{providerLabel(i.provider)}</Badge>
                      <Badge variant="info">{tk(`purpose.${i.purpose}`, i.purpose)}</Badge>
                      <Badge variant={statusVariant(i.status)}>{tk(`status.${i.status}`, i.status)}</Badge>
                    </div>
                    {i.lastError && i.status === 'error' && (
                      <p className="text-xs text-[#991B1B] mt-1 truncate">{i.lastError}</p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">{i.displayName}</p>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <div className="flex items-center gap-1.5 mr-1">
                      <Switch
                        checked={i.enabled}
                        disabled={busyId === i.id || i.status !== 'connected'}
                        onCheckedChange={() => handleToggle(i)}
                      />
                      <span className="text-xs text-muted-foreground">
                        {i.enabled ? tk('list.syncing', 'Syncing') : tk('list.paused', 'Paused')}
                      </span>
                    </div>
                    <Button variant="ghost" size="sm" disabled={busyId === i.id} onClick={() => handleTest(i)}>
                      <ShieldCheck className="w-4 h-4" /> {tk('list.test', 'Test')}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={busyId === i.id || i.status !== 'connected'}
                      onClick={() => handleSync(i)}
                    >
                      {busyId === i.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                      {tk('list.sync', 'Sync now')}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-[#991B1B] hover:text-[#991B1B] hover:bg-[#FEE2E2]"
                      disabled={busyId === i.id}
                      onClick={() => handleDisconnect(i)}
                    >
                      <Trash2 className="w-4 h-4" /> {tk('list.disconnect', 'Disconnect')}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

/** Collapsible IMAP/app-password connect form (for non-Google mail servers). */
const ImapConnect: React.FC<{
  purpose: MailboxPurpose;
  onConnected: () => void | Promise<void>;
  tk: (k: string, f: string, o?: Record<string, unknown>) => string;
}> = ({ purpose, onConnected, tk }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ email: '', host: 'imap.gmail.com', port: 993, password: '' });

  const submit = async () => {
    if (!form.email || !form.host || !form.password) {
      toast.error(tk('imap.required', 'Email, host and password are required'));
      return;
    }
    setSaving(true);
    try {
      const created = await createImapIntegration({
        displayName: form.email,
        emailAddress: form.email,
        purpose,
        host: form.host,
        port: Number(form.port) || 993,
        secure: true,
        password: form.password,
      });
      // Validate the login immediately so the row doesn't sit "pending".
      const test = await testIntegration(created.id);
      if (test.ok) {
        await enableIntegration(created.id);
        toast.success(tk('imap.connected', 'Mailbox connected'));
      } else {
        toast.error(test.error || tk('imap.test_failed', 'Login failed — check the app password'));
      }
      setForm({ email: '', host: 'imap.gmail.com', port: 993, password: '' });
      setOpen(false);
      await onConnected();
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, tk('imap.failed', 'Could not connect the mailbox')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-t border-border pt-4">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-charcoal"
      >
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
        {tk('imap.toggle', 'Connect another server via IMAP (app password)')}
      </button>
      {open && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
          <div className="space-y-2">
            <Label htmlFor="imap-email">{tk('imap.email', 'Email address')}</Label>
            <Input id="imap-email" type="email" value={form.email} placeholder="hr@company.co.tz"
              onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="imap-pass">{tk('imap.password', 'App password')}</Label>
            <Input id="imap-pass" type="password" value={form.password} placeholder="••••••••••••"
              onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="imap-host">{tk('imap.host', 'IMAP host')}</Label>
            <Input id="imap-host" value={form.host}
              onChange={(e) => setForm({ ...form, host: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="imap-port">{tk('imap.port', 'Port')}</Label>
            <Input id="imap-port" type="number" value={form.port}
              onChange={(e) => setForm({ ...form, port: Number(e.target.value) })} />
          </div>
          <div className="md:col-span-2">
            <Button onClick={submit} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plug className="w-4 h-4" />}
              {tk('imap.submit', 'Connect mailbox')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailIntegrationsPage;
